import { Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  popoverBoundedMaxWidthClass,
} from '@reflex/ui/components/ui/popover';
import { ToolbarIconButton } from '@reflex/ui/components/shared/ToolbarIconButton';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import type { PluginAttachmentValue } from '@/lib/types';
import { usePlugins } from '@/hooks/config/usePlugins';
import { AttachmentSummaryLabel } from '@/components/agent/attachments/AttachmentChipParts';
import { summaryOwnsIcon } from '@/components/agent/attachments/attachment-chip-utils';

/** Tiles rendered in the stack before the rest collapse into a "+N" tile. */
const MAX_TILES = 3;

/**
 * The label the "+" and the deck both hang off, so the two halves of the
 * control name the same thing.
 */
const STACK_LABEL = 'Context on the devbox';

export interface AgentContextStackProps {
  /**
   * Opens the shared `@`-mention menu, where the runtime-attachable categories
   * (MCP servers) attach to the live run. Omit on runs that cannot take one —
   * the stack then reads as a status display with no "+".
   */
  onAdd?: () => void;
  /** Attachments applied to the run, keyed by attachment id. */
  attachmentValues: Record<string, PluginAttachmentValue>;
}

/**
 * The live composer's context control: a "+" that opens the `@`-mention menu,
 * followed by a stack of overlapping tiles — one per kind of context on the
 * devbox (MCP servers, repo, network policy, …). The stack is deliberately not
 * the launch dialog's chip row: the composer toolbar is a single dense line
 * beside the agent/model/provider chip, and a running session accumulates
 * context the user can read but not take back, so the compact stack shows
 * *that* there is context and its popover shows *what*.
 *
 * Tiles carry the attachment's plugin icon rather than a per-item glyph, so one
 * tile means one kind however many servers it holds; the popover's summary is
 * where "GitHub +1" resolves into names.
 */
export function AgentContextStack({ onAdd, attachmentValues }: AgentContextStackProps) {
  const { attachments } = usePlugins();
  // `attachments` is registry-sorted by order/label, so the stack keeps a
  // stable left-to-right identity as items are attached mid-run.
  const applied = attachments.filter((def) => attachmentValues[def.id] !== undefined);
  const tiles = applied.slice(0, MAX_TILES);
  const overflow = applied.length - tiles.length;

  return (
    <div className="flex min-w-0 items-center gap-1">
      {onAdd ? (
        <ToolbarIconButton
          icon={Plus}
          label="Add context"
          data-testid="agent-context-add-button"
          onClick={onAdd}
        />
      ) : null}
      {/* Wraps the whole deck, not just the tiles inside it: the first
          attachment mounts this subtree and the last one unmounts it, so an
          `AnimatePresence` living below the `applied.length` gate could never
          run its own children's first enter or last exit. */}
      <AnimatePresence initial={false}>
        {applied.length > 0 && (
          <motion.div
            key="stack"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.12 }}
          >
            <Popover>
              {/*
                Deliberately no `Tooltip` wrapping this trigger. Nesting a
                Radix `Tooltip` (`TooltipTrigger asChild`) around the
                `PopoverTrigger` makes the popover close itself the instant
                the summary resolves and focus settles — Radix reads the
                tooltip's focus/blur cycle as focus-outside and dismisses the
                open popover in the same tick it appeared. The accessible
                name below covers screen readers; a visual tooltip can only
                come back as a composition validated against the
                `ContextStackPopoverStaysOpen` regression story. See #4067.
              */}
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="toolbar-icon"
                  aria-label={`${STACK_LABEL} (${applied.length})`}
                  data-testid="agent-context-stack"
                  // Wider than the square icon button it is sized from: the
                  // deck fans out past the first tile.
                  className="w-auto px-1.5"
                >
                  <span className="flex items-center">
                    <AnimatePresence initial={false}>
                      {tiles.map((def, index) => {
                        const Icon = def.icon;
                        return (
                          <motion.span
                            key={def.id}
                            layout
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.6 }}
                            transition={{ duration: 0.12 }}
                            data-testid={`agent-context-tile-${def.id}`}
                            // Later tiles slide under earlier ones, so the
                            // stack reads as a deck seen from its top card.
                            style={{ zIndex: tiles.length - index }}
                            className={cn(
                              'relative flex size-5 items-center justify-center rounded-[5px] border border-border bg-background text-foreground shadow-sm',
                              index > 0 && '-ml-1.5',
                            )}
                          >
                            <Icon className="size-2.5" />
                          </motion.span>
                        );
                      })}
                    </AnimatePresence>
                    {overflow > 0 ? (
                      <span
                        data-testid="agent-context-tile-overflow"
                        className="relative -ml-1.5 flex size-5 items-center justify-center rounded-[5px] border border-border bg-muted text-[9px] font-medium leading-none text-muted-foreground"
                      >
                        +{overflow}
                      </span>
                    ) : null}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                aria-label={STACK_LABEL}
                // Without a real cap a devbox carrying several kinds of
                // context renders rows the width of the viewport.
                className={`w-auto min-w-[14rem] ${popoverBoundedMaxWidthClass('22rem')} p-2`}
              >
                <SectionLabel as="p" className="px-1 pb-1.5">
                  {STACK_LABEL}
                </SectionLabel>
                <ul className="flex flex-col gap-1">
                  {applied.map((def) => {
                    const Icon = def.icon;
                    return (
                      <li
                        key={def.id}
                        data-testid={`agent-context-stack-item-${def.id}`}
                        className="flex min-w-0 items-center gap-2 rounded-md px-1 py-1 text-xs"
                      >
                        {/* The summary may lead with a value-specific icon of
                            its own (the MCP server's logo tile); showing the
                            kind icon too would put two glyphs on one short
                            row. */}
                        {summaryOwnsIcon(def) ? null : (
                          <Icon className="icon-xs shrink-0 text-muted-foreground" />
                        )}
                        <span className="shrink-0 text-muted-foreground">{def.label}</span>
                        <span className="flex min-w-0 items-center overflow-hidden">
                          <AttachmentSummaryLabel
                            definition={def}
                            config={attachmentValues[def.id]?.config ?? null}
                            fallback={<span className="truncate">Added</span>}
                          />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </PopoverContent>
            </Popover>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
